import {parseHeaderDisabled} from './components/SiteHeader/headerDenyListEntries';
import rnuc from './config.generated';

export enum Brand {
  BOTTLEMART = 'BM',
  SIPNSAVE = 'SNS',
  HARRY_BROWN = 'HB',
  THIRSTY_CAMEL = 'TC',
  DEVELOPMENT = 'DEV',
}

type Env = 'DEV' | 'BETA' | undefined;

/**
 * Extracts Play Store package from BUNDLE_ID
 * BUNDLE_ID can be:
 * - Simple string: "au.com.lmg.bottlemart"
 * - Object: { ios: "...", android: "au.com.lmg.bottlemart", web: "..." }
 *
 * Note: On native Android, use DeviceInfo.getBundleId() instead (storeLinks.ts does this)
 * This is primarily for Storybook/web testing purposes
 */
const getPlayStorePackageFromBundleId = (): string | undefined => {
  const bundleId = rnuc.BUNDLE_ID;
  if (!bundleId) return undefined;
  if (typeof bundleId === 'string') return bundleId;
  if (typeof bundleId === 'object' && bundleId.android) return bundleId.android;
  return undefined;
};

export const config = {
  APP_NAME: rnuc.APP_NAME,
  BASE_URL: rnuc.BASE_URL,
  BASE_TENANT: rnuc.BASE_TENANT as Brand,
  HOST_DOMAIN: rnuc.HOST_DOMAIN,
  TILL_GATEWAY: rnuc.TILL_GATEWAY,
  DEFAULT_STORE: rnuc.DEFAULT_STORE,
  DEFAULT_STORE_STATE: rnuc.DEFAULT_STORE_STATE,
  SUMMER_CASH_GRAB_COMPETITION_NAME: rnuc.SUMMER_CASH_GRAB_COMPETITION_NAME,
  GIFT_CARD_STORE: rnuc.GIFT_CARD_STORE,
  FF_IS_GIFT_CARDS_ENABLED: rnuc.FF_IS_GIFT_CARDS_ENABLED,
  /**
   * DIS-1897. Web SiteHeader deny-list: each listed entry is switched off for the
   * brand. `HEADER` keeps the legacy header; a `MEGA_MENU_*` entry switches off that
   * mega-menu item. Absent key or empty list = everything on. Unknown entries
   * are dropped by parseHeaderDisabled.
   */
  WEB_HEADER_DISABLED: parseHeaderDisabled(rnuc.WEB_HEADER_DISABLED),
  /**
   * DIS-1501-B. Gates the "all participating gift-card stores" behaviour on the
   * gift-card participating-stores page. OFF everywhere until the DIS-1503
   * middleware endpoint (extended per B1) is deployed for the tenant. Flag-OFF
   * keeps the legacy suburb-search path unchanged. Coerced defensively (mirrors
   * USE_API_PROXY): rnuc bakes a boolean, a quoted YAML value would surface a
   * string — accept either.
   */
  FF_GIFT_CARD_ALL_STORES:
    rnuc.FF_GIFT_CARD_ALL_STORES === 'true' || rnuc.FF_GIFT_CARD_ALL_STORES === true,
  GIFT_CARDS_PROMOTION_ID: rnuc.GIFT_CARDS_PROMOTION_ID,
  SUPPORT_EMAIL: rnuc.SUPPORT_EMAIL,
  STORYBOOK_ENABLED: rnuc.STORYBOOK_ENABLED || false,
  ONESIGNAL_APP_ID: rnuc.ONESIGNAL_APP_ID,
  WHITE_LABEL_ID: rnuc.WHITE_LABEL_ID,
  USE_API_PROXY: rnuc.USE_API_PROXY === 'true' || rnuc.USE_API_PROXY === true,
  API_PROXY_TARGET: rnuc.API_PROXY_TARGET || '',
  APP_STORE_ID: rnuc.APP_STORE_ID,
  /**
   * Play Store package derived from BUNDLE_ID (for Storybook/web testing)
   * On native Android, storeLinks.ts uses DeviceInfo.getBundleId() instead
   */
  PLAY_STORE_PACKAGE: getPlayStorePackageFromBundleId(),
  /**
   * This is used for loading mfl dev shops directly see shopSelectionStore. e.g. the app development stores
   * 'DEV'
   */
  ENV: rnuc.ENV as Env,

  /**
   * REQ-5 (DIS-1384): test-build-only OTP-bypass id. Baked from the env yml ONLY
   * in dev/automation profiles — absent (empty) in beta/prod builds. Consumed by
   * contextHeadersRequestInterceptor behind an additional ENV==='DEV' runtime
   * gate, so it can never be sent from a production build.
   */
  AUTOMATION_TEST_ID: (rnuc as {AUTOMATION_TEST_ID?: string}).AUTOMATION_TEST_ID || '',

  // Apple Pay Configuration
  // Note: APPLE_PAY_MERCHANT_ID is only used by iOS native (PassKit requires it in app)
  // Web flow does NOT need merchantId - backend handles it during merchant validation
  /** Apple Pay Merchant Identifier - iOS native only (e.g., 'merchant.com.lmg.sandbox') */
  APPLE_PAY_MERCHANT_ID: rnuc.APPLE_PAY_MERCHANT_ID || '',
  /** Apple Pay Merchant Display Name */
  APPLE_PAY_MERCHANT_NAME: rnuc.APPLE_PAY_MERCHANT_NAME || rnuc.APP_NAME || '',

  // Google Pay needs no env config on any platform: merchant identity (merchant_id,
  // merchant_origin, auth_jwt, software_info) is MFL-driven and arrives via
  // /me/payment-services/{id}/tokenization on every platform and ENV (DIS-1005).
  // Web visibility is gated by isReadyToPay (CRYPTOGRAM_3DS + existingPaymentMethodRequired)
  // with isLikelyMobileWeb() as a cheap pay.js pre-filter (DIS-1515). Desktop never
  // offers Google Pay; Android mobile web only when the capability probe passes.

  /**
   * Release version: TanStack Query persist cache buster, Sentry release, web footer stamp.
   * Web: webpack DefinePlugin replaces the exact expression `process.env.APP_VERSION` with the
   * CI APP_VERSION (or `git describe` locally). Keep it bare: a `typeof process` guard is not
   * replaced, and the browser has no `process`, so the guard fell through to MARKETING_VERSION.
   * Native: React Native's setUpGlobals defines `process.env` with NODE_ENV only, so APP_VERSION
   * is undefined and the value is MARKETING_VERSION.
   * Fallback: '0.0.0-dev' — prevents buster from becoming the string 'undefined'.
   */
  APP_VERSION: process.env.APP_VERSION || rnuc.MARKETING_VERSION || '0.0.0-dev',
};

export type Config = typeof config;
export default config;
